import { BookingServiceClient } from 'fitbeat.api-clients';
import { UserWorkoutSession } from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import {
  IBookingInviteContinue,
  ICancelBookingConfirmed,
  IChangeBookingSuccess,
  IMakeBookingSuccess,
  ISaveBagContactDuringCheckInSuccess,
  IStartOnDemandWorkoutPressedSuccess,
} from '../Bookings';
import { getApiConfig } from '../config';
import { IAppStore } from '../HomeWorkouts';
import { GoogleAnalytics } from '../services/google-analytics';
import { IDashboardOpenedSuccess } from '../SummaryDashboard/actions';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';

export const PLANNER_OPENED_REQUEST = 'PLANNER_OPENED_REQUEST';
export const PLANNER_OPENED_SUCCESS = 'PLANNER_OPENED_SUCCESS';
export const PLANNER_OPENED_FAILED = 'PLANNER_OPENED_FAILED';
export const PLANNER_OPENED = 'PLANNER_OPENED';
export const STUDIO_PARTNER_INVITE_ACCEPTED = 'STUDIO_PARTNER_INVITE_ACCEPTED';
export const STUDIO_PARTNER_INVITE_REJECTED = 'STUDIO_PARTNER_INVITE_REJECTED';
export const STUDIO_PARTNER_INVITE_CHANGED = 'STUDIO_PARTNER_INVITE_CHANGED';
export const PARTNER_BOOKING_UPDATED = 'PARTNER_BOOKING_UPDATED';
export const INFLUENCER_SCHEDULE_TILE_PRESSED = 'INFLUENCER_SCHEDULE_TILE_PRESSED';
export const INVITE_SCREEN_UNMOUNTED = 'INVITE_SCREEN_UNMOUNTED';

export const getDatesForNextTwoWeeks = (): moment.Moment[] => {
  const today = moment().startOf('day');
  return [...Array(14).keys()].map((key) => moment(today).add(key, 'day'));
};

export async function loadBookingsData(
  dispatch: (action: IPlannerOpenedRequest | IPlannerOpenedSuccess | IPlannerOpenedFailed) => void,
  userProfile,
) {
  try {
    dispatch(plannerOpenedRequest());
    const bookingServiceClient = new BookingServiceClient(
      getApiConfig(), fetch, new AuthTokenBuilder(), new GoogleAnalytics());
    const bookings = await bookingServiceClient.getMyBookings(userProfile.longTermGoal);
    dispatch(plannerOpenedSuccess(bookings));

  } catch (error) {
    dispatch(plannerOpenedFailed(error as Error));
  }
}

const loadPlannerDays = (dispatch: (action: IPlannerOpened) => void) => {
  const days = getDatesForNextTwoWeeks();
  dispatch(plannerOpenedActionCreator(days));
};

export const plannerOpened = (userProfile) => (
  async (
    dispatch: (action: IPlannerOpened | IPlannerOpenedRequest | IPlannerOpenedSuccess | IPlannerOpenedFailed) => void,
    getState: () => IAppStore,
  ) => {
    loadPlannerDays(dispatch);
    await loadBookingsData(dispatch, userProfile);
  });

const plannerOpenedActionCreator = (days: moment.Moment[]): IPlannerOpened => ({
  type: PLANNER_OPENED,
  payload: { days },
});

const plannerOpenedRequest = (): IPlannerOpenedRequest => ({
  type: PLANNER_OPENED_REQUEST,
});

const plannerOpenedSuccess = (bookings: any[]): IPlannerOpenedSuccess => ({
  type: PLANNER_OPENED_SUCCESS,
  payload: { bookings },
});

export const studioPartnerInviteAccepted = (acceptedInvite: UserWorkoutSession): IStudioPartnerInviteAccepted => ({
  type: STUDIO_PARTNER_INVITE_ACCEPTED,
  payload: { acceptedInvite },
});

export const studioPartnerInviteRejected = (inviteId: string): IStudioPartnerInviteRejected => ({
  type: STUDIO_PARTNER_INVITE_REJECTED,
  payload: { inviteId },
});

export const changeInviteSuccess = (workoutId: string, changedWorkout: UserWorkoutSession): IStudioPartnerChanged => ({
  type: STUDIO_PARTNER_INVITE_CHANGED,
  payload: {
    changedWorkout,
    workoutId,
  },
});

const plannerOpenedFailed = (error: Error): IPlannerOpenedFailed => ({
  type: PLANNER_OPENED_FAILED,
  payload: { error },
});

export const influencerScheduleTilePressed = (bookingTime: number, inviteId: string, referredByFullname) => {
  return {
    type: INFLUENCER_SCHEDULE_TILE_PRESSED,
    payload: {bookingTime, inviteId, referredByFullname},
  };
};

export const inviteScreenUnmounted = () => {
  return {
    type: INVITE_SCREEN_UNMOUNTED,
  };
};
interface IStudioPartnerChanged {
  type: typeof STUDIO_PARTNER_INVITE_CHANGED;
  payload: {
    changedWorkout: UserWorkoutSession;
    workoutId: string;
  };
}

interface IPlannerOpened {
  type: typeof PLANNER_OPENED;
  payload: {
    days: moment.Moment[];
  };
}

interface IPlannerOpenedRequest {
  type: typeof PLANNER_OPENED_REQUEST;
}

interface IPlannerOpenedSuccess {
  type: typeof PLANNER_OPENED_SUCCESS;
  payload: {
    bookings: any[];
  };
}

interface IStudioPartnerInviteAccepted {
  type: typeof STUDIO_PARTNER_INVITE_ACCEPTED;
  payload: {
    acceptedInvite: UserWorkoutSession;
  };
}

interface IStudioPartnerInviteRejected {
  type: typeof STUDIO_PARTNER_INVITE_REJECTED;
  payload: {
    inviteId: string;
  };
}
interface IPlannerOpenedFailed {
  type: typeof PLANNER_OPENED_FAILED;
  payload: {
    error: Error;
  };
}

export type PlannerActionTypes =
  IPlannerOpenedRequest
  | IPlannerOpenedSuccess
  | IPlannerOpenedFailed
  | IMakeBookingSuccess
  | IChangeBookingSuccess
  | ICancelBookingConfirmed
  | IDashboardOpenedSuccess
  | IBookingInviteContinue
  | IPlannerOpened
  | IStartOnDemandWorkoutPressedSuccess
  | ISaveBagContactDuringCheckInSuccess;
